import React, { useContext, useState, useRef, useEffect } from 'react';
import './Login.css';
import { StateContext } from '../StateProvider';
import { Container, Form, Button, Modal, Alert } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LetsGetStarted from '../Utilities/components/LetsGetStarted';
import Footer from '../Footer';
import OpenInBrowserPrompt from '../Utilities/components/OpenInBrowserPrompt';

import {
  signInWithGoogle,
  signInWithFacebook,
  signInWithEmailAndPass,
  signInWithSMS,
  verifyCode
} from './Firebase';

import { RecaptchaVerifier, getAuth } from 'firebase/auth';

import loginButtonGoogle from '../../assets/Login/loginButtonGoogle.png';
import loginButtonFacebook from '../../assets/Login/loginButtonFacebook.png';
import loginButtonPhone from '../../assets/Login/loginButtonPhone.png';

const auth = getAuth();

export const initializeRecaptcha = (recaptchaContainer) => {
  if (!window.recaptchaVerifier) {
    window.recaptchaVerifier = new RecaptchaVerifier(
      recaptchaContainer,
      {
        size: 'invisible',
        callback: function () {},
        'expired-callback': function () {
          console.error('Recaptcha expired');
        }
      },
      auth
    );

    window.recaptchaVerifier.render().catch(function (error) {
      console.error('Recaptcha render error:', error);
    });
  }
  return window.recaptchaVerifier;
};

function isInAppBrowser() {
  var ua = navigator.userAgent || navigator.vendor || window.opera;
  return !/(Chrome|Safari|Firefox|Edg|Opera)/i.test(ua);
}

const Login = function () {
  const { setLoggedIn, setLoading, setCurrentCommunity, communityMap } = useContext(StateContext);

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [error, setError] = useState('');
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  const [showBrowserPrompt, setShowBrowserPrompt] = useState(false);

  const recaptchaContainer = useRef(null);
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const community = searchParams.get('community');
  var displayCommunity = community;

  useEffect(() => {
      if (community) {
          setCurrentCommunity(community);
      } else {
          setCurrentCommunity(null);
      }
  }, [community]);

  useEffect(function () {
    if (!recaptchaVerifier && recaptchaContainer.current) {
      var verifier = initializeRecaptcha(recaptchaContainer.current);
      setRecaptchaVerifier(verifier);
    }
    return function () {
      if (recaptchaVerifier) {
        recaptchaVerifier.clear();
      }
    };
  }, [recaptchaVerifier]);

    // Get dynamic DB community object
  var comm;
  if (displayCommunity && communityMap && communityMap[displayCommunity]) {
    comm = communityMap[displayCommunity];
  }

    // Early return if community exists in URL but map not ready yet
    if (displayCommunity && Object.keys(communityMap).length === 0) {
        return <div style={{ padding: 40 }}>Loading community…</div>;
    }

  function handleChange(e) {
    setFormData(Object.assign({}, formData, { [e.target.name]: e.target.value }));
    setError('');
  }

  async function handleLogin(loginMethod) {
    setLoading(true);
    var response = await loginMethod();
    if (response.success) {
      if (response.confirmationResult) {
        setConfirmationResult(response.confirmationResult);
        setError('');
        setShowSMSModal(false);
        setShowCodeModal(true);
      } else {
        setLoggedIn(true);
        navigate('/');
        window.scrollTo(0, 0);
        setError('');
      }
    } else {
      setError(response.error);
      setLoggedIn(false);
    }
    setLoading(false);
  }

  function handleSubmit(e) {
    e.preventDefault();
    handleLogin(function () {
      return signInWithEmailAndPass(formData.email, formData.password);
    });
  }

  async function handleVerifySMSCode() {
    if (!confirmationResult) {
      setError('No confirmation available. Please request code again.');
      return;
    }

    setLoading(true);
    var response = await verifyCode(smsCode, confirmationResult);
    if (response.success) {
      setLoggedIn(true);
      navigate('/');
      window.scrollTo(0, 0);
      setError('');
      setShowCodeModal(false);
    } else {
      setError(response.error);
    }
    setLoading(false);
  }

  return (
    <>
      <Container className="login-container">

        {/* Dynamic Community Header */}
        {comm && (
        <div className="apartment-login-header">

            {/* Hero Image */}
            {comm.heroImage ? (
            <img
                src={comm.heroImage}
                alt=""
                className="apartment-hero"
            />
            ) : null}

            <h4>
            Welcome, {comm.name} Members!
            </h4>

            <p>Your community perks include:</p>

            {/* Perks */}
            {comm.perks && (
            <ul className="apartment-perks-list">
                {comm.perks.map(function (perk, idx) {
                return (
                    <li key={idx} className="apartment-perk-item">
                    {perk}
                    </li>
                );
                })}
            </ul>
            )}

            {/* Voucher */}
            {comm.voucherText ? (
            <p className="apartment-voucher">
                Enjoy {comm.voucherText} today!
            </p>
            ) : null}

        </div>
        )}

        {!displayCommunity && (
          <h5 className="login-header">Welcome, Paw Lovers!</h5>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        <div className="firebase-container">
          <Button
            onClick={function () {
              if (isInAppBrowser()) setShowBrowserPrompt(true);
              else handleLogin(signInWithGoogle);
            }}
            className="login-button-google"
          >
            <img
              src={loginButtonGoogle}
              alt="Google Login"
              className="login-button-image"
            />
            <p className="login-button-text">Google</p>
          </Button>

          <Button
            onClick={function () {
              return handleLogin(signInWithFacebook);
            }}
            className="login-button-facebook"
          >
            <img
              src={loginButtonFacebook}
              alt="Facebook Login"
              className="login-button-image"
            />
            <p className="login-button-text">Facebook</p>
          </Button>

          <Button
            onClick={function () {
              return setShowSMSModal(true);
            }}
            className="login-button-mobile"
          >
            <img
              src={loginButtonPhone}
              alt="Mobile Login"
              className="login-button-image"
            />
            <p className="login-button-text">Mobile</p>
          </Button>
        </div>

        {/* Email Login */}
        <Form onSubmit={handleSubmit}>
          <Form.Group className="login-email" controlId="email">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group className="login-password" controlId="password">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
            />
          </Form.Group>

          <Button type="submit" className="login-button">
            LOGIN
          </Button>
        </Form>

        <div className="login-message-container">
          <p>Do you need to create an account? <a href="/register">Register</a></p>
          <p>Forgot your password? <a href="/resetPassword">Reset Password</a></p>
        </div>

      </Container>

      {/* SMS Modal */}
      <Modal
        show={showSMSModal}
        onHide={function () { setShowSMSModal(false); }}
        className="sms-sign-in-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Enter Your Phone Number</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter phone number"
                value={phoneNumber}
                onChange={function (e) {
                  setPhoneNumber(e.target.value);
                }}
              />
            </Form.Group>

            {confirmationResult && (
              <Form.Group>
                <Form.Label>Enter Verification Code</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Verification code"
                  value={smsCode}
                  onChange={function (e) {
                    setSmsCode(e.target.value);
                  }}
                />
                <Button onClick={handleVerifySMSCode}>
                  Verify Code
                </Button>
              </Form.Group>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            className="sms-sign-in-modal-close-button"
            onClick={function () {
              setShowSMSModal(false);
            }}
          >
            Close
          </Button>

          {!confirmationResult && (
            <Button
              variant="primary"
              className="sms-sign-in-modal-send-button"
              onClick={function () {
                return handleLogin(function () {
                  return signInWithSMS(phoneNumber, 'recaptcha-container');
                });
              }}
            >
              Send Verification Code
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* SMS Verification Modal */}
      <Modal
        show={showCodeModal}
        onHide={function () { setShowCodeModal(false); }}
        className="sms-code-verification-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Verify Your Phone Number</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Verification Code</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter verification code"
                value={smsCode}
                onChange={function (e) { setSmsCode(e.target.value); }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            className="sms-code-verification-modal-close-button"
            onClick={function () {
              setShowCodeModal(false);
            }}
          >
            Close
          </Button>

          <Button
            variant="primary"
            className="sms-code-verification-modal-verify-button"
            onClick={handleVerifySMSCode}
          >
            Verify Code
          </Button>
        </Modal.Footer>
      </Modal>

      {showBrowserPrompt && <OpenInBrowserPrompt />}

      <section className="sign-up-section">
        <LetsGetStarted />
      </section>

      <Footer />
    </>
  );
};

export default Login;
